import { useState, useMemo } from 'react';
import { Group, Text, Button, Menu, ThemeIcon, ScrollArea } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';

import { ListWorkOrderDto } from '../models/workorder.models';
import classes from '../Components/WorkOrders/WorkOrderList.module.css';

type SortKey = 'Status' | 'WorkOrderNumber' | 'Created' | 'Updated' | 'Deleted' | 'DueDate' | 'CompletedDate' | 'Priority' | 'Unread';

export enum SortOptions {
	Created = 'Created',
	Updated = 'Updated',
	Deleted = 'Deleted',
	Completed = 'Completed',
	DueDate = 'DueDate',
	Priority = 'Priority',
	Status = 'Status',
	WorkOrderNumber = 'WorkOrderNumber',
	Unread = 'Unread'
}

const DEFAULT_SORT_OPTIONS = [
	SortOptions.Created,
	SortOptions.Unread,
	SortOptions.Updated,
	SortOptions.Completed,
	SortOptions.DueDate,
	SortOptions.Priority,
	SortOptions.Status,
	SortOptions.WorkOrderNumber
];

export function useSortedWorkOrders(workOrders: ListWorkOrderDto[], storageKey: string, sortOptions: SortOptions[] = DEFAULT_SORT_OPTIONS) {
	const USE_SORT_OPTION_CREATED = sortOptions.includes(SortOptions.Created);
	const USE_SORT_OPTION_UPDATED = sortOptions.includes(SortOptions.Updated);
	const USE_SORT_OPTION_DELETED = sortOptions.includes(SortOptions.Deleted);
	const USE_SORT_OPTION_COMPLETED = sortOptions.includes(SortOptions.Completed);
	const USE_SORT_OPTION_DUE_DATE = sortOptions.includes(SortOptions.DueDate);
	const USE_SORT_OPTION_PRIORITY = sortOptions.includes(SortOptions.Priority);
	const USE_SORT_OPTION_STATUS = sortOptions.includes(SortOptions.Status);
	const USE_SORT_OPTION_WORK_ORDER_NUMBER = sortOptions.includes(SortOptions.WorkOrderNumber);
	const USE_SORT_OPTION_UNREAD = sortOptions.includes(SortOptions.Unread);

	const getDefaultSortKey = (): SortKey => {
		if (USE_SORT_OPTION_CREATED) return 'Created';
		if (USE_SORT_OPTION_UPDATED) return 'Updated';
		if (USE_SORT_OPTION_DELETED) return 'Deleted';
		if (USE_SORT_OPTION_COMPLETED) return 'CompletedDate';
		if (USE_SORT_OPTION_DUE_DATE) return 'DueDate';
		if (USE_SORT_OPTION_PRIORITY) return 'Priority';
		if (USE_SORT_OPTION_STATUS) return 'Status';
		if (USE_SORT_OPTION_WORK_ORDER_NUMBER) return 'WorkOrderNumber';
		if (USE_SORT_OPTION_UNREAD) return 'Unread';
		return 'Created';
	};

	const [sortKey, setSortKey] = useLocalStorage<SortKey>({
		key: `workorder-sorttype-${storageKey}`, 
		defaultValue: getDefaultSortKey()
	});
	const [sortOrder, setSortOrder] = useLocalStorage<'asc' | 'desc'>({
		key: `workorder-sortorder-${storageKey}`,
		defaultValue: 'desc'
	});
	// const [search, setSearch] = useState<string>('');


	const sortedList = useMemo(() => {
	    //    let filtered = workOrders;
	    //    if (search) {
		//        filtered = filtered.filter(wo =>
		// 	       wo.Title.toLowerCase().includes(search.toLowerCase())
		//        );
	    //    }
		return [...workOrders].sort((a, b) => {
			let compare = 0;
			if (sortKey === 'Created') {
				compare = new Date(a.Created).getTime() - new Date(b.Created).getTime();
			} else if (sortKey === 'Updated') {
				if (a.Updated && b.Updated) {
					compare = new Date(a.Updated).getTime() - new Date(b.Updated).getTime();
				}
			} else if (sortKey === 'Deleted') {
				if (a.Deleted && b.Deleted) {
					compare = new Date(a.Deleted).getTime() - new Date(b.Deleted).getTime();
				}
			} else if (sortKey == 'DueDate') {
				if (!a.DueDate && b.DueDate) {
					compare = 1;
				} else if (a.DueDate && !b.DueDate) {
					compare = -1;
				} else if (a.DueDate && b.DueDate) {
					compare = new Date(a.DueDate).getTime() - new Date(b.DueDate).getTime();
				}
			} else if (sortKey == 'CompletedDate') {
				if (a.CompletedDate && b.CompletedDate) {
					compare = new Date(a.CompletedDate).getTime() - new Date(b.CompletedDate).getTime();
				}
			} else if (sortKey == 'WorkOrderNumber') {
				if (parseInt(a.WorkOrderNumber) < parseInt(b.WorkOrderNumber)) compare = -1;
				else if (parseInt(a.WorkOrderNumber) > parseInt(b.WorkOrderNumber)) compare = 1;
				else compare = 0;
			} else if (sortKey == 'Priority') {
				if (a.Priority.Sort < b.Priority.Sort) compare = -1;
				else if (a.Priority.Sort > b.Priority.Sort) compare = 1;
				else compare = 0;
			} else if (sortKey == 'Unread') {
				if (a.Read === b.Read) compare = 0;
				else if (a.Read && !b.Read) compare = 1;
				else if (!a.Read && b.Read) compare = -1;
			} else {
				if (a[sortKey] < b[sortKey]) compare = -1;
				else if (a[sortKey] > b[sortKey]) compare = 1;
				else compare = 0;
			}
			return sortOrder === 'asc' ? compare : -compare;
		});
    }, [workOrders, sortKey]);

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>

	const SortOptionsDropdown = useMemo(() => <>
		<Group px="md" py="5" gap={0} wrap="nowrap" className={classes.sortGroup}>
			<Text fz="sm" visibleFrom="xs">Sort by:</Text>
			<Text fz="sm" hiddenFrom="xs">Sort:</Text>
			<Menu radius="lg" closeOnItemClick={false} closeOnClickOutside={true} withArrow offset={-5} shadow="lg" width={170}>
				<Menu.Target>
					<Button variant="transparent">
						{sortKey === "Created" ? (sortOrder === 'asc' ? "Created Date: Oldest First" : "Created Date: Newest First") : ""}
						{sortKey === "Updated" ? (sortOrder === 'asc' ? "Last Updated: Oldest First" : "Last Updated: Newest First") : ""}
						{sortKey === "Deleted" ? (sortOrder === 'asc' ? "Deleted Date: Oldest First" : "Deleted Date: Newest First") : ""}
						{sortKey === "DueDate" ? (sortOrder === 'asc' ? "Due Date: Oldest First" : "Due Date: Newest First") : ""}
						{sortKey === "CompletedDate" ? (sortOrder === 'asc' ? "Completed Date: Oldest First" : "Completed Date: Newest First") : ""}
						{sortKey === "Priority" ? (sortOrder === 'asc' ? "Priority: Lowest First" : "Priority: Highest First") : ""}
						{sortKey === "Status" ? (sortOrder === 'asc' ? "Status: Ascending" : "Status: Descending") : ""}
						{sortKey === "WorkOrderNumber" ? (sortOrder === 'asc' ? "Work Order #: Lowest First" : "Work Order #: Highest First") : ""}
						{sortKey === "Unread" ? (sortOrder === 'asc' ? "Unread First" : "Read First") : ""}
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					<ScrollArea h={Math.min(300, 100 * sortOptions.length + 7 * (sortOptions.length - 1))} type="always" scrollbarSize="8" scrollbars="y">
					{USE_SORT_OPTION_CREATED && <>
					<Menu.Label>Creation Date</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Created'); setSortOrder('desc'); }} rightSection={sortKey === 'Created' && sortOrder === 'desc' && checkIcon}>
						Newest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Created'); setSortOrder('asc'); }} rightSection={sortKey === 'Created' && sortOrder === 'asc' && checkIcon}>
						Oldest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_UNREAD && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Read Status</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Unread'); setSortOrder('asc'); }} rightSection={sortKey === 'Unread' && sortOrder === 'asc' && checkIcon}>
						Unread First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Unread'); setSortOrder('desc'); }} rightSection={sortKey === 'Unread' && sortOrder === 'desc' && checkIcon}>
						Read First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_UPDATED && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Last Updated</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Updated'); setSortOrder('desc'); }} rightSection={sortKey === 'Updated' && sortOrder === 'desc' && checkIcon}>
						Newest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Updated'); setSortOrder('asc'); }} rightSection={sortKey === 'Updated' && sortOrder === 'asc' && checkIcon}>
						Oldest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_DELETED && <>
					{/* {sortOptions.length > 1 && <Menu.Divider />} */}
					<Menu.Label>Deleted Date</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Deleted'); setSortOrder('desc'); }} rightSection={sortKey === 'Deleted' && sortOrder === 'desc' && checkIcon}>
						Newest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Deleted'); setSortOrder('asc'); }} rightSection={sortKey === 'Deleted' && sortOrder === 'asc' && checkIcon}>
						Oldest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_DUE_DATE && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Due Date</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('DueDate'); setSortOrder('desc'); }} rightSection={sortKey === 'DueDate' && sortOrder === 'desc' && checkIcon}>
						Newest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('DueDate'); setSortOrder('asc'); }} rightSection={sortKey === 'DueDate' && sortOrder === 'asc' && checkIcon}>
						Oldest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_COMPLETED && <>
					{/* {sortOptions.length > 1 && <Menu.Divider />} */}
					<Menu.Label>Completed Date</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('CompletedDate'); setSortOrder('desc'); }} rightSection={sortKey === 'CompletedDate' && sortOrder === 'desc' && checkIcon}>
						Newest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('CompletedDate'); setSortOrder('asc'); }} rightSection={sortKey === 'CompletedDate' && sortOrder === 'asc' && checkIcon}>
						Oldest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_PRIORITY && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Priority</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Priority'); setSortOrder('desc'); }} rightSection={sortKey === 'Priority' && sortOrder === 'desc' && checkIcon}>
						Highest First
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Priority'); setSortOrder('asc'); }} rightSection={sortKey === 'Priority' && sortOrder === 'asc' && checkIcon}>
						Lowest First
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_STATUS && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Status</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('Status'); setSortOrder('asc'); }} rightSection={sortKey === 'Status' && sortOrder === 'asc' && checkIcon}>
						Ascending
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('Status'); setSortOrder('desc'); }} rightSection={sortKey === 'Status' && sortOrder === 'desc' && checkIcon}>
						Descending
					</Menu.Item>
					</>}
					{USE_SORT_OPTION_WORK_ORDER_NUMBER && <>
					{sortOptions.length > 1 && <Menu.Divider />}
					<Menu.Label>Work Order #</Menu.Label>
					<Menu.Item onClick={() => { setSortKey('WorkOrderNumber'); setSortOrder('asc'); }} rightSection={sortKey === 'WorkOrderNumber' && sortOrder === 'asc' && checkIcon}>
						Ascending
					</Menu.Item>
					<Menu.Item onClick={() => { setSortKey('WorkOrderNumber'); setSortOrder('desc'); }} rightSection={sortKey === 'WorkOrderNumber' && sortOrder === 'desc' && checkIcon}>
						Descending
					</Menu.Item>
					</>}
					</ScrollArea>
				</Menu.Dropdown>
			</Menu>
		</Group>
	</>, [sortKey, sortOrder]);

	return { sortedList, SortOptionsDropdown };
}